import Link from "next/link";
import type { CSSProperties } from "react";
import { HubArrow, HubIcon } from "@/app/_components/conditions/hub/hub-icons";
import motion from "@/app/_components/conditions/hub/hub-motion.module.css";
import type { AreaRow } from "@/lib/areas/row-model";
import { AreaTodayLabel, AreaTodayStyle } from "../AreaDayName";
import { AreaRowRank, AreaRowWhileCurrent } from "../AreaRankClock";
import type {
  AreaPageViewModel,
  AreaViewHeatCell,
  AreaViewHeatmap,
  AreaViewHeatRow,
  AreaViewSnowDay,
  AreaViewSnowWatch,
  AreaViewSportPanel,
} from "../area-view-types";
import { AreaChevron, AreaDifficultyPill, AreaPill, AreaSectionHead } from "./area-a-parts";
import {
  AREA_BOARD_CAP_PHONE,
  HEAT_SLATE_SWATCHES,
  heatBandsShown,
  heatBg,
  heatFg,
  hr12,
  nextRefreshClock,
  snowChart,
  snowSummary,
  snowTotalLine,
  snowWatchHeading,
  thousands,
  unitCount,
  unitSingular,
  updatedAt,
  zoneWord,
  type AreaBoardManifestRow,
  type SnowChart,
} from "./area-g3-format";
import { AreaBoardControls, type AreaBoardGroup } from "./AreaBoardControls";
import { AreaSnowWatchPicker, type AreaSnowWatchOption } from "./AreaSnowWatchPicker";
import { G3_CARD } from "./g3-parts";

/**
 * "This week, spot by spot" (G4) and the off-season First snow watch. Client-safe components
 * (no server imports, no hooks of their own): the page renders its own sport's board on the
 * server, and the on-demand sport panels (AreaSportOnDemand) render the same board in the browser
 * from the panel route's payload. Never a cross-spot "best day": "Picks for your plan" (AreaPicks)
 * replaces the G3 best windows list under the board.
 */

function cellFlag(cell: AreaViewHeatCell): "red" | "amber" | null {
  return cell.safety === "red" || cell.safety === "amber" ? cell.safety : null;
}

function lowConfidence(cell: AreaViewHeatCell): boolean {
  return cell.score100 !== null && !cell.eligible && cell.safety !== "red";
}

function cellAria(cell: AreaViewHeatCell, weekday: string): string {
  if (cell.score100 === null) return `${weekday} no score`;
  const flag = cellFlag(cell);
  return `${weekday} ${cell.score100}${flag === "red" ? " red safety flag" : flag === "amber" ? " amber safety flag" : ""}${lowConfidence(cell) ? " early estimate" : ""}`;
}

/** Today's ring, applied by AreaTodayStyle to the cells of the viewer's today. */
const TODAY_RING_CSS = "box-shadow:0 0 0 2px #FFFFFF,0 0 0 4px #16808F";

function EmptyCell({ dateISO }: Readonly<{ dateISO: string }>) {
  return <span aria-hidden="true" data-heat-date={dateISO} className="flex h-9 items-center justify-center rounded-[8px] border border-dashed border-[#D5DBD2] md:h-12 md:rounded-[10px]" />;
}

function HeatCell({ cell }: Readonly<{ cell: AreaViewHeatCell }>) {
  if (cell.score100 === null) return <EmptyCell dateISO={cell.dateISO} />;
  const flag = cellFlag(cell);
  const style: CSSProperties = { background: heatBg(cell.score100), color: heatFg(cell.score100) };
  return (
    <span
      aria-hidden="true"
      data-heat-cell={cell.score100}
      data-heat-date={cell.dateISO}
      style={style}
      className="relative flex h-9 flex-col items-center justify-center gap-px rounded-[8px] text-[12.5px] font-bold md:h-12 md:rounded-[10px] md:text-[15px]"
    >
      {lowConfidence(cell) && (
        <span data-heat-low="" className="absolute inset-0 rounded-[inherit] bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.32)_0_4px,transparent_4px_9px)]" />
      )}
      <span className="relative">{cell.score100}</span>
      {cell.window && <span className="relative hidden text-[10.5px] font-medium md:block">from {hr12(cell.window[0])}</span>}
      {flag && (
        <span
          data-heat-flag={flag}
          className={`absolute right-[3px] top-[3px] size-2 rounded-full border-[1.5px] border-white ${flag === "red" ? "bg-[#C2332F]" : "bg-[#E3A21A]"}`}
        />
      )}
    </span>
  );
}

function snowCmText(cm: number | null): string {
  if (cm === null) return "";
  if (cm <= 0) return "0";
  if (cm < 1) return "<1";
  return String(Math.round(cm));
}

function SnowRows({ snow, daysStyle }: Readonly<{ snow: readonly AreaViewSnowDay[]; daysStyle: CSSProperties }>) {
  const maxSnow = Math.max(1, ...snow.map((day) => day.maxSnowCm ?? 0));
  const hasFreezing = snow.some((day) => day.freezingM !== null);
  return (
    <div aria-hidden="true" data-heat-snow="" className="mt-2.5 border-t border-dashed border-[#D5DBD2] px-1.5 pt-2.5 md:px-2">
      <div className={`${BOARD_ROW} items-end`} style={daysStyle}>
        <span className="col-span-2 mb-1 text-[12px] font-bold leading-[1.2] text-navy md:mb-0 md:text-[13px]">
          New snow, cm
          <span className="ml-1.5 text-[10.5px] font-medium text-[#566072] md:ml-0 md:block md:text-[11px]">most at any resort</span>
        </span>
        <span className={BOARD_CELLS}>
          {snow.map((day) => {
            const cm = day.maxSnowCm ?? 0;
            const bar = cm > 0 ? Math.max(3, Math.round((cm / maxSnow) * 14)) : 2;
            return (
              <span key={day.dateISO} className="flex flex-col items-center gap-0.5">
                <span className="text-[11.5px] font-bold text-teal-dark md:text-[12.5px]">{snowCmText(day.maxSnowCm)}</span>
                <span className={`block w-[60%] max-w-[22px] rounded-[2px] ${cm > 0 ? "bg-[#5BAFCB]" : "bg-[#D5DBD2]"}`} style={{ height: bar }} />
              </span>
            );
          })}
        </span>
      </div>
      {hasFreezing && (
        <div className={`${BOARD_ROW} mt-2 items-center`} style={daysStyle}>
          <span className="col-span-2 mb-1 text-[12px] font-bold leading-[1.2] text-navy md:mb-0 md:text-[13px]">Freezing level, km</span>
          <span className={BOARD_CELLS}>
            {snow.map((day) => (
              <span key={day.dateISO} className="text-center text-[11.5px] text-text-body md:text-[12.5px]">
                {day.freezingM === null ? "" : (Math.round(day.freezingM / 100) / 10).toFixed(1)}
              </span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
}

/** The board's day columns: phone cells span the card, desktop puts rank and name before them. */
const BOARD_ROW = "grid grid-cols-[22px_minmax(0,1fr)] gap-x-2 md:grid-cols-[30px_minmax(0,270px)_minmax(0,1fr)] md:items-center md:gap-x-2.5";
const BOARD_CELLS = "col-span-2 grid grid-cols-[repeat(var(--days),minmax(0,1fr))] gap-[5px] md:col-span-1 md:gap-1.5";

const UPDATING_PILL = (
  <span data-area-row-updating="" className="inline-flex h-[20px] items-center rounded-full bg-[#EDEFF2] px-2 text-[11.5px] font-semibold text-[#566072]">
    Updating
  </span>
);

function boardNoun(panel: AreaViewSportPanel, kind: AreaPageViewModel["kind"]): string {
  if (panel.unit === "beaches") return "beach";
  if (panel.unit === "spots" && kind === "mountain") return "resort";
  return unitSingular(panel.unit);
}

function compareLabel(panel: AreaViewSportPanel): string {
  if (panel.isPrimary && panel.key === "surf") return "Compare with every surf spot worldwide";
  if (panel.isPrimary && panel.key === "piste") return "Compare with every ski resort worldwide";
  return `Compare ${panel.lowerName} worldwide`;
}

function rowAria(row: AreaViewHeatRow, panel: AreaViewSportPanel, days: AreaViewHeatmap["days"]): string {
  const today = row.cells[0];
  const todayText = row.status === "stale"
    ? "today's score updating"
    : today?.score100 != null
      ? `today${today.band ? ` ${today.band.label}` : ""}, ${panel.lowerName} GO score ${today.score100} out of 100`
      : null;
  const parts = [row.rank !== null ? `Rank ${row.rank}, ${row.name}` : row.name, row.difficulty?.label ?? null, todayText, row.statsLine ?? null].filter(Boolean);
  const week = row.cells.length > 0 ? ` This week: ${row.cells.map((cell, index) => cellAria(cell, days[index]?.weekday ?? "")).join(", ")}.` : "";
  return `${parts.join(", ")}.${week} Open hourly detail`;
}

function BoardRow({ row, panel, days, daysStyle, index }: Readonly<{
  row: AreaViewHeatRow;
  panel: AreaViewSportPanel;
  days: AreaViewHeatmap["days"];
  daysStyle: CSSProperties;
  index: number;
}>) {
  const sport = panel.key;
  const ranked = row.status !== "stale" && row.rank !== null;
  return (
    <li
      data-area-row={row.slug}
      data-heat-row={row.slug}
      data-status={row.status ?? "ranked"}
      className={`${index < AREA_BOARD_CAP_PHONE ? motion.row : ""} border-t border-[#EEF0EB] first:border-t-0`}
      style={index < AREA_BOARD_CAP_PHONE ? { animationDelay: `${index * 40}ms` } : undefined}
    >
      <Link
        href={row.href}
        aria-label={rowAria(row, panel, days)}
        className={`${BOARD_ROW} rounded-[12px] px-1.5 py-2.5 text-text-body no-underline transition-colors hover:bg-[#F4F8F7] md:min-h-[64px] md:px-2 md:py-1.5`}
        style={daysStyle}
      >
        <span aria-hidden="true" className="self-start pt-px text-center text-[15px] font-bold text-navy md:self-center md:text-[16px]">
          {ranked && row.rank !== null ? <AreaRowRank sport={sport} slug={row.slug} rank={row.rank} bare /> : null}
        </span>
        <span aria-hidden="true" className="flex min-w-0 flex-col gap-[3px]">
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 truncate text-[15px] font-bold leading-[1.2] text-teal-dark underline decoration-[rgba(21,114,127,0.35)] underline-offset-[3px] md:text-[15px]">
              {row.shortName}
            </span>
            {row.difficulty && <AreaDifficultyPill label={row.difficulty.label} tone={row.difficulty.tone} />}
            {row.status === "stale" && UPDATING_PILL}
            {ranked && (
              <AreaRowWhileCurrent sport={sport} slug={row.slug} fallback={UPDATING_PILL}>
                {null}
              </AreaRowWhileCurrent>
            )}
            {row.closing && <AreaPill tone={row.closing.startsWith("Past") ? "neutral" : "pale"} className="text-[11.5px]">{row.closing}</AreaPill>}
            {row.note && <AreaPill tone="teal" className="text-[11.5px]">{row.note}</AreaPill>}
          </span>
          {row.statsLine && <span className="truncate text-[12.5px] leading-[1.3] text-[#566072]">{row.statsLine}</span>}
        </span>
        {row.cells.length > 0 && (
          <span aria-hidden="true" className={`${BOARD_CELLS} mt-2 md:mt-0`}>
            {row.cells.map((cell, cellIndex) => (
              // Day 0 is the board's score: withdrawn with the row's rank once it stops being
              // current on the browser's clock.
              cellIndex === 0 && ranked ? (
                <AreaRowWhileCurrent key={cell.dateISO} sport={sport} slug={row.slug} fallback={<EmptyCell dateISO={cell.dateISO} />}>
                  <HeatCell cell={cell} />
                </AreaRowWhileCurrent>
              ) : (
                <HeatCell key={cell.dateISO} cell={cell} />
              )
            ))}
          </span>
        )}
      </Link>
    </li>
  );
}

function CollapsedRows({ title, rows }: Readonly<{ title: string; rows: readonly AreaRow[] }>) {
  if (rows.length === 0) return null;
  return (
    <details data-area-collapsed={title} className="group mx-3 mb-2 rounded-[14px] border border-border bg-warm-bg last:mb-0 md:mx-[18px]">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-3.5 py-2 text-[14px] font-semibold text-navy [&::-webkit-details-marker]:hidden">
        <span>{title} <span className="font-normal text-[#566072]">({rows.length})</span></span>
        <span className="flex text-teal-dark transition-transform group-open:rotate-180"><AreaChevron /></span>
      </summary>
      <ul className="m-0 list-none px-2 pb-2 pt-0">
        {rows.map((row) => (
          <li key={row.slug} data-area-row={row.slug} data-status={row.status}>
            <Link href={row.href} className="flex min-h-11 items-center justify-between gap-3 rounded-[10px] px-2 text-[14px] text-navy no-underline hover:bg-white">
              <span className="truncate font-semibold">{row.displayName}</span>
              {row.label && <AreaPill>{row.label}</AreaPill>}
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * "This week, spot by spot" (G4): the sport's one board. Every ranked spot in today's true rank
 * order (then any whose score is Updating), each with its section, ability pill (surf family),
 * today's compact stats and its own score per day, inside `data-nosnippet`. Section chips, the
 * surf ability select and "Show all" (8 on phones, 10 from md) live in AreaBoardControls; the
 * browser clock re-judges each ranked row (rank and today's cell), and only the browser marks
 * today's column. Closed and unscored rows sit in collapsed groups below. Client-safe (no server
 * imports): the on-demand sport panels render it from the panel route's payload.
 */
export function AreaWeekBoard({
  panel,
  view,
}: Readonly<{ panel: AreaViewSportPanel; view: Pick<AreaPageViewModel, "slug" | "name" | "kind" | "timezone"> }>) {
  const heatmap = panel.heatmap;
  const { ranked } = panel;
  const rows = heatmap?.rows ?? [];
  const days = heatmap?.days ?? [];
  const noun = boardNoun(panel, view.kind);
  const nouns = panel.unit === "beaches" ? "beaches" : noun === "resort" ? "resorts" : panel.unit;
  const headingId = `area-week-h-${panel.key}`;
  const title = `This week, ${noun === "beach" ? "spot" : noun} by ${noun === "beach" ? "spot" : noun}`;
  const eyebrow = panel.isPrimary ? "This week" : `This week · ${panel.label}`;
  const lead = `Each ${noun}'s own forecast score per day, in today's rank order. Tap a ${noun} for hourly detail.`;
  const updated = updatedAt(panel.updatedISO, view.timezone);
  // An empty board says so once, in its empty state below: no "0 resorts ... in rank order" line.
  const listed = rows.length > 0
    ? `${unitCount(rows.length, panel.unit === "spots" && noun === "resort" ? "resorts" : panel.unit)} for ${panel.lowerName} in today's true rank order`
    : null;
  const subDesktop = [listed, updated].filter(Boolean).join(" · ");
  const subPhone = listed
    ? [listed, updated?.replace(/^Updated /u, "").replace(/ (UK|local) time$/u, "")].filter(Boolean).join(" · ")
    : updated?.replace(/ (UK|local) time$/u, "") ?? "";
  const daysStyle = { "--days": String(Math.max(1, days.length)) } as CSSProperties;
  const scope = `${panel.key}-${view.slug}`;
  const heatScope = `heat-${panel.key}`;
  const cells = rows.flatMap((row) => row.cells);
  const maxScore = Math.max(0, ...cells.map((cell) => cell.score100 ?? 0));
  const bands = heatBandsShown(maxScore);
  const anyFlag = cells.some((cell) => cellFlag(cell) !== null);
  const anyLow = cells.some(lowConfidence);
  const anyWindow = cells.some((cell) => cell.window !== null);
  const closed = ranked.rows.filter((row) => row.status === "withheld" || row.status === "closed");
  const unscored = ranked.rows.filter((row) => row.status === "unscored");
  const manifest: AreaBoardManifestRow[] = rows.map((row) => ({ slug: row.slug, group: row.group ?? "", experiencedOnly: row.experiencedOnly ?? false }));
  const groups: AreaBoardGroup[] = ranked.groups.filter((group) => group.count > 0).map((group) => ({ key: group.key, name: group.name, count: group.count }));
  const refresh = nextRefreshClock(panel.updatedISO, view.timezone);
  const thin = panel.state === "ranked" && ranked.rankedCount < 6 && ranked.rankedCount < panel.memberCount;
  const compareHref = panel.boardHref.split("#")[0] || panel.boardHref;
  const caption = maxScore < 55
    ? `Every score this week is under 55, so cells use the slate band, darker for higher. Days are not compared across ${nouns === "beaches" ? "spots" : nouns}.`
    : `Cells use the ${panel.lowerName} score bands, the same colours as the rings. Days are not compared across ${nouns === "beaches" ? "spots" : nouns}.`;

  const header = (
    <div className="hidden items-start gap-3.5 px-[22px] pb-1 pt-5 md:flex">
      <span aria-hidden="true" className="flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-navy text-teal-light">
        <HubIcon name={panel.icon} size={22} strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-teal-dark">{eyebrow}</p>
        <h2 id={headingId} className="mt-0.5 text-[22px] font-bold leading-[1.2] text-navy">{title}</h2>
        <p className="mt-1 text-[14px] leading-[1.5] text-[#566072]">{lead}</p>
        <p className="mt-1 text-[14px] font-semibold leading-[1.45] text-teal-dark">{subDesktop}</p>
      </div>
    </div>
  );

  return (
    <div data-area-board-panel={panel.key} data-area-heatmap={panel.key} data-heat-scope={heatScope}>
      <AreaTodayStyle scope={heatScope} timezone={view.timezone} css={TODAY_RING_CSS} />
      <div className="md:hidden">
        <AreaSectionHead id={`${headingId}-phone`} eyebrow={eyebrow} title={title} lead={lead} tick={false} />
        <p className="mt-1 text-[13.5px] font-semibold leading-[1.4] text-teal-dark">{subPhone}</p>
      </div>
      <div data-nosnippet="" data-area-ranked={panel.key} role="region" aria-labelledby={headingId} className={`${G3_CARD} mt-3.5 md:mt-0`}>
        <AreaBoardControls
          scope={scope}
          tone="light"
          header={header}
          footerLink={(
            <Link href={compareHref} className="group inline-flex min-h-11 items-center gap-1.5 px-1 text-[14px] font-bold text-teal-dark underline underline-offset-[3px] md:text-[14.5px]">
              {compareLabel(panel)}
              <HubArrow />
            </Link>
          )}
          after={(
            <div className="px-3 pb-3.5 md:px-[18px] md:pb-[18px]">
              {(closed.length + unscored.length > 0) && (
                <div className="-mx-3 mb-3 md:-mx-[18px]">
                  <CollapsedRows title="Closed today" rows={closed} />
                  <CollapsedRows title="No current score" rows={unscored} />
                </div>
              )}
              {cells.length > 0 && (
                <>
                  <div aria-hidden="true" className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t border-[#EEF0EB] pt-2.5 text-[11.5px] text-[#566072] md:gap-x-5 md:gap-y-2 md:pt-3 md:text-[12.5px]">
                    <span className="inline-flex items-center gap-1 md:gap-1.5">
                      {bands.length > 0 ? "Under 55" : "Lower"}
                      <span className="inline-flex gap-0.5">
                        {HEAT_SLATE_SWATCHES.map((colour) => (
                          <span key={colour} className="h-3 w-[18px] rounded-[3px]" style={{ background: colour }} />
                        ))}
                      </span>
                      {bands.length > 0 ? null : "Higher"}
                    </span>
                    {bands.map((band) => (
                      <span key={band.label} className="inline-flex items-center gap-[5px]">
                        <span className="h-3 w-[18px] rounded-[3px]" style={{ background: band.colour }} />
                        {band.label}
                      </span>
                    ))}
                    {anyFlag && (
                      <>
                        <span className="inline-flex items-center gap-[5px]"><span className="size-2 rounded-full bg-[#E3A21A]" />Amber<span className="hidden md:inline"> safety</span> flag</span>
                        <span className="inline-flex items-center gap-[5px]"><span className="size-2 rounded-full bg-[#C2332F]" />Red<span className="hidden md:inline"> safety</span> flag</span>
                      </>
                    )}
                    {anyLow && (
                      <span className="inline-flex items-center gap-[5px]">
                        <span className="h-3 w-[18px] rounded-[3px] bg-[#8B93A0] bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.4)_0_3px,transparent_3px_6px)]" />
                        Early estimate (low confidence)
                      </span>
                    )}
                    {anyWindow && <span className="hidden md:inline">Small time: start of that day&apos;s best three hours</span>}
                  </div>
                  <p className="mt-1.5 text-[11.5px] leading-[1.45] text-[#566072] md:text-[12.5px]">{caption}</p>
                </>
              )}
            </div>
          )}
          groups={groups}
          groupLabel={ranked.groupLabel}
          allLabel={`All ${nouns}`}
          unitPlural={nouns}
          manifest={manifest}
          showAbility={ranked.abilityFilter && rows.length > 0}
          abilityNote={!ranked.abilityFilter && view.kind !== "mountain" && rows.length > 0 ? "Ability ratings are for surf and bodyboarding only." : null}
        >
          {panel.state === "stale" && (
            <p role="note" className="mx-3 mt-2 rounded-[12px] border border-[#E8C766] bg-[#FFF6DB] p-3.5 text-[14px] leading-[1.5] text-[#6B4E00] md:mx-[18px]">
              These {panel.lowerName} scores have not refreshed recently, so no rank or score is shown today. Open a {noun} for its own latest forecast.
            </p>
          )}
          {panel.state === "updating" && (
            <p role="note" className="mx-3 mt-2 rounded-[12px] border border-[#BFDDE1] bg-[#F2F8F8] p-3.5 text-[14px] leading-[1.5] text-text-body md:mx-[18px]">
              Today&apos;s {panel.lowerName} scores are on their way{refresh ? `, by ${refresh} ${zoneWord(view.timezone)}` : ""}. Until then each {noun} reads Updating.
            </p>
          )}
          {rows.length === 0 ? (
            <p className="mx-3 my-2 rounded-[12px] bg-warm-bg p-3.5 text-[14px] text-[#566072] md:mx-[18px]">
              No {noun} here has a current {panel.lowerName} score.{" "}
              <Link href={panel.boardHref} className="font-semibold text-teal-dark underline">{panel.boardLabel}</Link>
            </p>
          ) : (
            <div className="px-2 md:px-3.5" style={daysStyle}>
              <div aria-hidden="true" className={`${BOARD_ROW} px-1.5 pb-1 pt-1 md:px-2`} style={daysStyle}>
                <span className="col-span-2 hidden self-end text-[11px] font-bold uppercase tracking-[0.1em] text-[#566072] md:col-span-2 md:block">
                  Rank, {noun} and today
                </span>
                {days.length > 0 && (
                  <span className={BOARD_CELLS}>
                    {days.map((day) => (
                      <span key={day.dateISO} data-heat-day={day.dateISO} className="flex flex-col items-center justify-end gap-px text-[12px] font-bold text-navy md:text-[13px]">
                        <AreaTodayLabel dateISO={day.dateISO} timezone={view.timezone} className="text-[9.5px] uppercase tracking-[0.06em] text-teal-dark md:text-[10px]" />
                        <span>
                          {day.weekday}
                          <span className="hidden font-medium text-[#566072] md:inline"> {day.dayOfMonth}</span>
                        </span>
                      </span>
                    ))}
                  </span>
                )}
              </div>
              <ol aria-label={`${view.name} ${nouns} for ${panel.lowerName} in today's rank order, with each day's score this week`} className="m-0 list-none p-0">
                {rows.map((row, index) => (
                  <BoardRow key={row.slug} row={row} panel={panel} days={days} daysStyle={daysStyle} index={index} />
                ))}
              </ol>
              {heatmap?.snow && heatmap.snow.length === days.length && days.length > 0 && <SnowRows snow={heatmap.snow} daysStyle={daysStyle} />}
            </div>
          )}
          {thin && (
            <div className="mx-3 mt-2 rounded-[14px] border border-border bg-warm-bg px-4 py-3.5 md:mx-[18px]">
              <p className="text-[14px] leading-[1.5] text-text-body">
                <strong className="text-navy">Only {ranked.rankedCount} of {unitCount(panel.memberCount, panel.unit)} have a {panel.lowerName} score today.</strong>{" "}
                The rest are left out here rather than ranked low.
              </p>
              <Link href={panel.boardHref} className="group mt-1 inline-flex min-h-11 items-center gap-1.5 text-[14px] font-bold text-teal-dark underline underline-offset-[3px]">
                {panel.boardLabel}
                <HubArrow />
              </Link>
            </div>
          )}
        </AreaBoardControls>
      </div>
    </div>
  );
}

// ── First snow watch (off-season mountains) ─────────────────────────────

function SnowChartSvg({ chart, label, className }: Readonly<{ chart: SnowChart; label: string; className: string }>) {
  const { plot } = chart;
  return (
    <div role="img" aria-label={label} className={className}>
      <svg viewBox={`0 0 ${chart.width} ${chart.height}`} width="100%" aria-hidden="true" focusable="false" className="block max-w-full">
        {chart.snowTicks.map((tick) => (
          <g key={`s-${tick.label}`}>
            <line x1={plot.left} y1={tick.y} x2={plot.right} y2={tick.y} stroke="#EDF1EC" strokeWidth="1" />
            <text x={plot.left - 6} y={tick.y + 4} fontSize="11" fill="#566072" textAnchor="end">{tick.label}</text>
          </g>
        ))}
        {chart.freezeTicks.map((tick) => (
          <text key={`f-${tick.label}`} x={plot.right + 6} y={tick.y + 4} fontSize="11" fill="#566072">{tick.label}</text>
        ))}
        <text x="0" y="10" fontSize="10.5" fontWeight="700" fill="#15727F">Snow, cm</text>
        <text x={chart.width} y="10" fontSize="10.5" fontWeight="700" fill="#8E4C30" textAnchor="end">Freezing level, m</text>
        {chart.heightLine && (
          <g>
            <line x1={plot.left} y1={chart.heightLine.y} x2={plot.right} y2={chart.heightLine.y} stroke="#16233B" strokeWidth="1.2" strokeDasharray="4 4" opacity="0.55" />
            <text
              x={plot.right - 4}
              y={chart.heightLine.labelY}
              fontSize="10.5"
              fontWeight="700"
              fill="#16233B"
              textAnchor="end"
              stroke="#FFFFFF"
              strokeWidth="3.5"
              strokeLinejoin="round"
              paintOrder="stroke"
            >
              {chart.heightLine.label}
            </text>
          </g>
        )}
        {chart.bars.map((bar) => (
          <g key={`b-${bar.cx}`}>
            <rect x={bar.x} y={bar.y} width={bar.width} height={bar.height} rx={bar.snowLabel ? 5 : 1.5} fill={bar.snowLabel ? "#5BC0CE" : "#D5DBD2"} />
            {bar.snowLabel && <text x={bar.cx} y={bar.y - 6} fontSize="12" fontWeight="700" fill="#16233B" textAnchor="middle">{bar.snowLabel}</text>}
            <text x={bar.cx} y={plot.bottom + 20} fontSize="12" fontWeight={600} fill="#16233B" textAnchor="middle">{bar.dayLabel}</text>
            {bar.tempLabel && <text x={bar.cx} y={plot.bottom + 37} fontSize="11" fill="#566072" textAnchor="middle">{bar.tempLabel}</text>}
          </g>
        ))}
        {chart.line.length > 1 && (
          <polyline points={chart.line.map((point) => `${point.x},${point.y}`).join(" ")} fill="none" stroke="#C17B5D" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {chart.line.map((point) => (
          <circle key={`p-${point.x}`} cx={point.x} cy={point.y} r="3.4" fill="#FFFFFF" stroke="#C17B5D" strokeWidth="2" />
        ))}
      </svg>
    </div>
  );
}

/** The off-season "First snow watch" card, with a forecast point picker. Null without weather. */
export function AreaSnowWatch({ watch, areaName }: Readonly<{ watch: AreaViewSnowWatch; areaName: string }>) {
  // The highest five forecast points (each draws a phone and a desktop chart: page weight).
  const members = watch.members.filter((member) => member.days.length > 0).slice(0, 5);
  if (members.length === 0) return null;
  const headingId = "area-snow-watch-h";
  const options: AreaSnowWatchOption[] = members.map((member) => {
    const label = member.elevationM !== null ? `${member.name} (${thousands(member.elevationM)} m)` : member.name;
    const phone = snowChart({ days: watch.days, weather: member.days, elevationM: member.elevationM, width: 326, height: 196 });
    const desktop = snowChart({ days: watch.days, weather: member.days, elevationM: member.elevationM, width: 700, height: 196 });
    const total = snowTotalLine(member.days);
    const aria = `${member.name} snow forecast for the next seven days: ${total}. ${snowSummary({ name: member.name, weather: member.days })}`;
    return {
      slug: member.slug,
      label,
      header: (
        <div className="flex flex-col gap-0.5">
          <p className="text-[15.5px] font-bold text-navy">{member.name}</p>
          <p className="text-[13px] text-[#566072]">
            {member.elevationM !== null ? `Forecast at ${thousands(member.elevationM)} m · ` : ""}{total}
          </p>
        </div>
      ),
      body: (
        <div>
          <SnowChartSvg chart={phone} label={aria} className="mt-3 md:hidden" />
          <SnowChartSvg chart={desktop} label={aria} className="mt-4 hidden md:block" />
          <div aria-hidden="true" className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-[#566072] md:text-[12.5px]">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-3 rounded-[2px] bg-teal-light" />New snow (cm)</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 rounded bg-terracotta" />Freezing level (m)</span>
            {member.elevationM !== null && <span className="inline-flex items-center gap-1.5"><span className="w-4 border-t border-dashed border-navy" />Forecast height</span>}
            <span>Max/min °C under each day</span>
          </div>
          <p className="mt-3 rounded-[12px] bg-[#F4F6F2] px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-text-body md:text-[14px]">
            {snowSummary({ name: member.name, weather: member.days })}
          </p>
        </div>
      ),
    };
  });
  return (
    <div data-area-snow-watch="">
      <AreaSectionHead
        id={headingId}
        eyebrow="Snow watch"
        title={snowWatchHeading(watch)}
        lead={`Forecast snowfall and freezing level for the next seven days at each ${areaName} resort's forecast point.`}
      />
      <div className={`${G3_CARD} mt-4 px-3.5 pb-3 pt-3.5 md:px-[22px] md:pb-[18px] md:pt-5`}>
        <AreaSnowWatchPicker scope="area-snow-watch" options={options} />
      </div>
    </div>
  );
}

